import React, { CSSProperties, FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { extractReceiptViaLocalModel, parseStatementViaLocalModel } from './lib/ai';
import { fetchBillingStatus, openCustomerPortal, startCheckout } from './lib/billing';
import './index.css';
import Tesseract from 'tesseract.js';
import {
  Plus,
  Upload,
  FileText,
  CheckCircle2,
  AlertCircle,
  Trash2,
  Undo2,
  ChevronLeft,
  ChevronRight,
  Download,
  Receipt as ReceiptIcon,
  Table as TableIcon,
  Search,
  Pencil,
  Camera,
  X,
  Menu,
  LogOut,
  Image as ImageIcon,
  Save,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { addMonths, compareAsc, format, parseISO, startOfMonth } from 'date-fns';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { motion, AnimatePresence } from 'motion/react';
import { Session } from '@supabase/supabase-js';
import { cn } from './lib/utils';
import { BillingStatus, Receipt, StatementFile, StatementOverlay, Transaction, ViewMode } from './types';
import { isSupabaseConfigured, supabase } from './lib/supabase';

const makeId = () => Math.random().toString(36).slice(2, 11);
const normalizeAmount = (value: string) => Number(value.replace(/[^\d.-]/g, ''));
const toDisplayDate = (value: string) => value ? format(parseISO(value), 'MM/dd/yyyy') : format(new Date(), 'MM/dd/yyyy');
const toLongDisplayDate = (value: string) => value ? format(parseISO(value), 'EEE, MMM d, yyyy') : format(new Date(), 'EEE, MMM d, yyyy');

const inferMerchantFromText = (text: string, fallback: string) => {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const candidate = lines.find((line) => /[A-Za-z]/.test(line) && !/invoice|receipt|date|time|total|subtotal|tax|change|cash|visa|mastercard/i.test(line));

  return candidate?.replace(/[^A-Za-z0-9 '&.,-]/g, '').trim() || fallback;
};

const inferAmountFromText = (text: string) => {
  const totalLine = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find((line) => /\btotal\b/i.test(line) && /\$?\d+[\d,.]*\.\d{2}/.test(line));

  if (totalLine) {
    const totalMatch = totalLine.match(/\$?\d+[\d,.]*\.\d{2}/g);
    if (totalMatch?.length) {
      return normalizeAmount(totalMatch[totalMatch.length - 1]);
    }
  }

  const allAmounts = text.match(/\$?\d+[\d,.]*\.\d{2}/g) || [];
  if (allAmounts.length) {
    return Math.max(...allAmounts.map(normalizeAmount).filter((value) => Number.isFinite(value)));
  }

  return 0;
};

const isMobileCameraSupported = () => {
  if (typeof window === 'undefined') return false;
  const hasCameraApi = Boolean(navigator.mediaDevices?.getUserMedia);
  const mobileUA = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  return hasCameraApi && mobileUA;
};

const buildFileFromBlob = (blob: Blob, filename: string) => new File([blob], filename, { type: blob.type || 'image/jpeg' });
const isProcessableImage = (file: File) => file.type.startsWith('image/') || /\.(png|jpe?g|webp|gif|heic|heif)$/i.test(file.name);
type StatementPdfEditorHandle = {
  undoLastPlacement: () => void;
};

const captureFullVideoFrame = (video: HTMLVideoElement) => {
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not capture camera frame.');
  ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
  return canvas.toDataURL('image/jpeg', 0.98);
};

const compressImageFile = async (file: File) => {
  if (typeof window === 'undefined' || !file.type.startsWith('image/')) return file;

  const imageUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Failed to load image for compression.'));
      el.src = imageUrl;
    });

    const maxDimension = 1600;
    const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
    const width = Math.max(1, Math.round(img.width * scale));
    const height = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
};

const readFileAsDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (event) => resolve(String(event.target?.result || ''));
  reader.onerror = () => reject(new Error(`Failed reading ${file.name}`));
  reader.readAsDataURL(file);
});

const shouldRunBrowserOcr = (file: File, base64Image: string) => {
  if (typeof window === 'undefined') return false;
  const mobileUA = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  if (mobileUA) return false;
  return file.size <= 4 * 1024 * 1024 && base64Image.length <= 6_000_000;
};


const safeIsoDate = (value?: string) => {
  if (!value) return format(new Date(), 'yyyy-MM-dd');
  const parsed = parseISO(value);
  return Number.isNaN(parsed.getTime()) ? format(new Date(), 'yyyy-MM-dd') : value;
};

if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).toString();
}

const extractTextFromPdf = async (file: File) => {
  const dataUrl = await readFileAsDataUrl(file);
  const pdfBase64 = dataUrl.split(',')[1];

  if (!pdfBase64) {
    throw new Error('Failed to read PDF file.');
  }

  const response = await fetch('/.netlify/functions/extract-receipt', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      mode: 'statement-pdf',
      pdfBase64,
    }),
  });

  const payload = await response.json();
  if (!response.ok) {
    throw new Error(payload?.error || 'Failed to extract statement PDF text.');
  }

  return payload?.text || '';
};

const inferStatementYear = (text: string) => {
  const statementDateMatch = text.match(/STATEMENT DATE\s+(\d{2})\/(\d{2})\/(\d{2,4})/i);
  if (!statementDateMatch) return new Date().getFullYear();
  const year = Number(statementDateMatch[3]);
  return year < 100 ? 2000 + year : year;
};

const monthKeyForDate = (date: string) => format(startOfMonth(parseISO(safeIsoDate(date))), 'yyyy-MM');
const toMonthLabel = (monthKey: string) => {
  const parsed = parseISO(`${monthKey}-01`);
  return Number.isNaN(parsed.getTime()) ? format(new Date(), 'MMMM yyyy') : format(parsed, 'MMMM yyyy');
};

function downloadDataUrl(filename: string, dataUrl?: string) {
  if (!dataUrl) return;
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  link.click();
}

function slugifyFilename(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'receipt';
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export default function App() {
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [statementFiles, setStatementFiles] = useState<StatementFile[]>([]);
  const [session, setSession] = useState<Session | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('dashboard');
  const [isUploading, setIsUploading] = useState(false);
  const [isImportingStatement, setIsImportingStatement] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploadNotice, setUploadNotice] = useState('');
  const [debugSteps, setDebugSteps] = useState<string[]>([]);
  const [statementError, setStatementError] = useState('');
  const [syncMessage, setSyncMessage] = useState('');
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const [showCameraOverlay, setShowCameraOverlay] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [isCaptureProcessing, setIsCaptureProcessing] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [isMobileCamera, setIsMobileCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [capturedFileName, setCapturedFileName] = useState('camera-capture.jpg');
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [isBillingLoading, setIsBillingLoading] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [generateWarnings, setGenerateWarnings] = useState<string[] | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Receipt | null>(null);
  const [statementDeleteTarget, setStatementDeleteTarget] = useState<StatementFile | null>(null);
  const [previewReceipt, setPreviewReceipt] = useState<Receipt | null>(null);
  const [statementLabelDraft, setStatementLabelDraft] = useState<Record<string, string>>({});
  const [statementLabelPosition, setStatementLabelPosition] = useState<Record<string, { x: number; y: number }>>({});
  const [statementFontSize, setStatementFontSize] = useState<Record<string, number>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const statementInputRef = useRef<HTMLInputElement>(null);
  const statementEditorRef = useRef<StatementPdfEditorHandle | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const cropperRef = useRef<any>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const [canUndoStatementLabel, setCanUndoStatementLabel] = useState(false);

  useEffect(() => {
    setIsMobileCamera(isMobileCameraSupported());
  }, []);

  useEffect(() => {
    if (!supabase) {
      setIsAuthLoading(false);
      return;
    }

    let isMounted = true;

    supabase.auth.getSession().then(({ data, error }) => {
      if (!isMounted) return;
      if (error) console.error('Session load failed:', error);
      setSession(data.session ?? null);
      setIsAuthLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!isMounted) return;
      setSession(nextSession);
      setIsAuthLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const stopCamera = () => {
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
      setIsCameraReady(false);
      setCameraError('');
    };

    if (!showCameraOverlay) {
      stopCamera();
      return;
    }

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
        mediaStreamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (error: any) {
        console.error('Camera start failed:', error);
        setCameraError(error?.message || 'Unable to access the camera.');
      }
    };

    startCamera();
    return stopCamera;
  }, [showCameraOverlay]);

  useEffect(() => {
    if (!uploadNotice) return;
    const timeout = window.setTimeout(() => setUploadNotice(''), 10000);
    return () => window.clearTimeout(timeout);
  }, [uploadNotice]);

  useEffect(() => {
    const loadSupabaseData = async () => {
      if (!supabase) {
        setSyncMessage('Supabase is not configured. Uploads are disabled until it is connected.');
        setReceipts([]);
        setTransactions([]);
        setStatementFiles([]);
        return;
      }

      if (!session?.user) {
        setSyncMessage('Sign in to access your saved receipts and statements.');
        setReceipts([]);
        setTransactions([]);
        setStatementFiles([]);
        setBillingStatus(null);
        return;
      }

      setIsSyncing(true);
      setSyncMessage('');

      try {
        const [{ data: receiptRows, error: receiptError }, { data: transactionRows, error: transactionError }, { data: statementRows, error: statementLoadError }] = await Promise.all([
          supabase.from('receipts').select('*').eq('user_id', session.user.id).order('date', { ascending: true }),
          supabase.from('transactions').select('*').eq('user_id', session.user.id).order('date', { ascending: true }),
          supabase.from('statements').select('*').eq('user_id', session.user.id).order('month_key', { ascending: true }),
        ]);

        if (receiptError) throw receiptError;
        if (transactionError) throw transactionError;
        if (statementLoadError) throw statementLoadError;

        if (receiptRows) {
          setReceipts(receiptRows.map((row: any) => ({
            id: row.id,
            storeName: row.store_name,
            date: row.date,
            amount: Number(row.amount ?? 0),
            summary: row.summary ?? '',
            wtmNumber: row.wtn_number ?? '',
            imageUrl: row.image_url ?? undefined,
            isExtracted: true,
            monthKey: monthKeyForDate(row.date),
            orderIndex: Number(row.order_index ?? 0),
          })));
        }

        if (transactionRows) {
          setTransactions(transactionRows.map((row: any) => ({
            id: row.id,
            date: row.date,
            merchant: row.merchant,
            amount: Number(row.amount ?? 0),
            wtmNumber: row.wtn_number ?? '',
            monthKey: monthKeyForDate(row.date),
          })));
        }

        if (statementRows) {
          setStatementFiles(statementRows.map((row: any) => ({
            id: row.id,
            monthKey: row.month_key,
            fileName: row.file_name,
            fileUrl: row.file_url,
            uploadedAt: row.created_at,
            overlays: Array.isArray(row.overlays) ? row.overlays : [],
          })));
        }

        setSyncMessage('Supabase connected');
      } catch (error) {
        console.error('Supabase load failed:', error);
        setReceipts([]);
        setTransactions([]);
        setStatementFiles([]);
        setSyncMessage('Could not load account data from Supabase.');
      } finally {
        setIsSyncing(false);
      }
    };

    loadSupabaseData();
  }, [session]);

  useEffect(() => {
    const loadBillingStatus = async () => {
      if (!session?.user || !supabase) {
        setBillingStatus(null);
        return;
      }

      setIsBillingLoading(true);
      try {
        const status = await fetchBillingStatus();
        setBillingStatus(status);
      } catch (error) {
        console.error('Billing status load failed:', error);
        setBillingStatus({
          hasSubscription: false,
          status: null,
          currentPeriodEnd: null,
          priceId: null,
          customerPortalReady: false,
        });
      } finally {
        setIsBillingLoading(false);
      }
    };

    loadBillingStatus();
  }, [session]);

  const enrichReceiptInBackground = (draftReceipt: Receipt, file: File, base64Image: string) => {
    void (async () => {
      let extractedStore = draftReceipt.storeName;
      let extractedDate = draftReceipt.date;
      let extractedAmount = draftReceipt.amount;
      let extractedSummary = draftReceipt.summary;
      let extractedText = draftReceipt.ocrText || '';

      try {
        const localModel = await Promise.race([
          extractReceiptViaLocalModel(base64Image, file.type || 'image/jpeg'),
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Model timeout')), 10000)),
        ]);

        extractedStore = localModel.storeName || extractedStore;
        extractedDate = safeIsoDate(localModel.date || extractedDate);
        extractedAmount = Number(localModel.amount ?? extractedAmount ?? 0);
        extractedSummary = localModel.summary || extractedSummary;
        extractedText = localModel.ocrText || extractedText;
      } catch (error) {
        console.error('Local model extraction failed:', error);
      }

      if (shouldRunBrowserOcr(file, base64Image)) {
        try {
          const result = await Promise.race([
            Tesseract.recognize(base64Image, 'eng', { logger: () => undefined }),
            new Promise<never>((_, reject) => setTimeout(() => reject(new Error('OCR timeout')), 8000)),
          ]);
          const text = result.data.text || '';
          if (text.trim()) {
            extractedText = text;
            extractedStore = inferMerchantFromText(text, extractedStore);
            if (!extractedAmount) extractedAmount = inferAmountFromText(text);
            if (!draftReceipt.summary) {
              extractedSummary = extractedSummary || '';
            }
          }
        } catch (error) {
          console.error('Browser OCR failed:', error);
        }
      }

      let updatedReceipt: Receipt | undefined;
      setReceipts((prev) => prev.map((receipt) => {
        if (receipt.id !== draftReceipt.id) return receipt;
        const nextDate = safeIsoDate(extractedDate || receipt.date);
        const shouldKeepManualSummary = Boolean(receipt.summary && receipt.summary !== draftReceipt.summary);
        const shouldKeepManualMerchant = receipt.storeName && receipt.storeName !== draftReceipt.storeName;
        const shouldKeepManualDate = receipt.date && receipt.date !== draftReceipt.date;
        const shouldKeepManualAmount = Number(receipt.amount || 0) !== Number(draftReceipt.amount || 0);
        updatedReceipt = {
          ...receipt,
          storeName: shouldKeepManualMerchant ? receipt.storeName : (extractedStore || receipt.storeName),
          date: shouldKeepManualDate ? receipt.date : nextDate,
          amount: shouldKeepManualAmount ? receipt.amount : (Number.isFinite(extractedAmount) ? extractedAmount : receipt.amount),
          summary: shouldKeepManualSummary ? receipt.summary : (extractedSummary || receipt.summary || ''),
          ocrText: extractedText || receipt.ocrText || '',
          isExtracted: true,
          monthKey: monthKeyForDate(shouldKeepManualDate ? receipt.date : nextDate),
        };
        return updatedReceipt;
      }));

      try {
        if (updatedReceipt) {
          await saveReceiptToSupabase(updatedReceipt);
          setSelectedMonth(updatedReceipt.monthKey || monthKeyForDate(updatedReceipt.date));
        }
      } catch (error) {
        console.error('Updated receipt sync failed:', error);
      }
      setUploadNotice(updatedReceipt.ocrText ? 'Receipt uploaded. OCR details were filled in automatically.' : 'Receipt uploaded. Add details manually if needed.');
    })();
  };

  const uploadReceiptImageToSupabase = async (receiptId: string, file: File) => {
    if (!supabase || !session?.user) throw new Error('Supabase login required for receipt uploads.');
    return await readFileAsDataUrl(file);
  };

  const saveReceiptToSupabase = async (receipt: Receipt) => {
    if (!supabase || !session?.user) throw new Error('Supabase login required for receipt uploads.');
    const payload = {
      id: receipt.id,
      user_id: session.user.id,
      store_name: receipt.storeName,
      date: receipt.date,
      amount: receipt.amount,
      summary: receipt.summary,
      wtn_number: receipt.wtmNumber,
      image_url: receipt.imageUrl,
      order_index: receipt.orderIndex ?? 0,
    };
    const { error } = await supabase.from('receipts').upsert(payload);
    if (error) throw new Error(error.message || 'Failed to save receipt to Supabase.');
  };

  const saveTransactionsToSupabase = async (items: Transaction[]) => {
    if (!supabase || !session?.user) throw new Error('Supabase login required for statement uploads.');
    if (items.length === 0) return;
    const { error } = await supabase.from('transactions').upsert(items.map((item) => ({
      id: item.id,
      user_id: session.user.id,
      date: item.date,
      merchant: item.merchant,
      amount: item.amount,
      wtn_number: item.wtmNumber,
    })));
    if (error) throw error;
  };

  const saveStatementFileToSupabase = async (statement: StatementFile) => {
    if (!supabase || !session?.user) throw new Error('Supabase login required for statement uploads.');
    const { error } = await supabase.from('statements').upsert({
      id: statement.id,
      user_id: session.user.id,
      month_key: statement.monthKey,
      file_name: statement.fileName,
      file_url: statement.fileUrl,
      overlays: statement.overlays ?? [],
    });
    if (error) {
      throw new Error(error.message || 'Failed to save statement record to Supabase.');
    }
  };

  const createReceiptFromFile = async (file: File) => {
    if (!supabase || !session?.user) {
      throw new Error('Please sign in to upload receipts. Uploads are saved to your Supabase account only.');
    }

    const optimizedFile = await compressImageFile(file);
    const base64 = await readFileAsDataUrl(optimizedFile);
    const baseDate = format(new Date(), 'yyyy-MM-dd');
    const storeName = file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim() || 'Uploaded Receipt';
    const receiptId = makeId();
    const imageUrl = await uploadReceiptImageToSupabase(receiptId, optimizedFile);
    const draftReceipt: Receipt = {
      id: receiptId,
      storeName,
      date: baseDate,
      amount: 0,
      summary: '',
      wtmNumber: '',
      imageUrl,
      isExtracted: false,
      ocrText: '',
      monthKey: monthKeyForDate(baseDate),
      orderIndex: visibleReceipts.length,
    };

    await saveReceiptToSupabase(draftReceipt);
    setReceipts((prev) => [draftReceipt, ...prev]);
    setSelectedMonth(draftReceipt.monthKey);
    enrichReceiptInBackground(draftReceipt, optimizedFile, base64);
    return draftReceipt;
  };

  const processReceiptFiles = async (files: FileList | File[]) => {
    const fileList = Array.from(files).filter(isProcessableImage);
    if (!fileList.length) throw new Error('No supported image files were selected.');

    for (const file of fileList) {
      setDebugSteps((prev) => [...prev, `Selected ${file.name || 'receipt image'}.`]);
      await createReceiptFromFile(file);
      setDebugSteps((prev) => [...prev, `Added ${file.name || 'receipt image'} to receipts.`]);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    setShowUploadOptions(false);
    setUploadError('');
    setUploadNotice('');
    setDebugSteps([]);
    setIsUploading(true);
    try {
      await processReceiptFiles(files);
      setUploadNotice('Receipt uploaded successfully. You can edit details immediately.');
    } catch (error) {
      console.error('Receipt upload failed:', error);
      setUploadError(error instanceof Error ? error.message : 'Receipt upload failed. Try again with a valid image file.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCameraCapture = async () => {
    const video = videoRef.current;
    if (!video) return;
    setCameraError('');
    setIsCaptureProcessing(true);
    try {
      setCapturedImage(captureFullVideoFrame(video));
      setCapturedFileName(`camera-capture-${Date.now()}.jpg`);
      setShowCameraOverlay(false);
      setShowUploadOptions(false);
    } catch (error: any) {
      console.error('Capture failed:', error);
      setCameraError(error?.message || 'Capture failed. Please try again.');
    } finally {
      setIsCaptureProcessing(false);
    }
  };

  const finalizeManualCrop = async () => {
    if (!capturedImage) return;
    setUploadError('');
    setIsUploading(true);
    try {
      const response = await fetch(capturedImage);
      const blob = await response.blob();
      const file = buildFileFromBlob(blob, capturedFileName);
      await processReceiptFiles([file]);
      setCapturedImage(null);
    } catch (error) {
      console.error('Camera save failed:', error);
      setUploadError('Camera upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleStatementUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStatementError('');
    setIsImportingStatement(true);
    try {
      if (!supabase || !session?.user) {
        throw new Error('Please sign in to upload statements. Uploads are saved to your Supabase account only.');
      }
      const isPdfStatement = file.type === 'application/pdf' || file.type === 'application/x-pdf' || /\.pdf$/i.test(file.name);
      if (!isPdfStatement) {
        throw new Error('Please upload the exact PDF statement file.');
      }
      const statementId = makeId();
      const monthKey = selectedMonth || format(new Date(), 'yyyy-MM');
      const fileUrl = await readFileAsDataUrl(file);
      const statementRecord: StatementFile = {
        id: statementId,
        monthKey,
        fileName: file.name,
        fileUrl,
        uploadedAt: new Date().toISOString(),
      };
      await saveStatementFileToSupabase(statementRecord);
      setStatementFiles((prev) => [statementRecord, ...prev.filter((item) => item.monthKey !== monthKey)]);

      try {
        const statementText = await extractTextFromPdf(file);
        const parsedTransactions = await parseStatementViaLocalModel(statementText);
        if (Array.isArray(parsedTransactions) && parsedTransactions.length) {
          const newTransactions: Transaction[] = parsedTransactions.map((t: any) => ({
            id: makeId(),
            date: safeIsoDate(t.date),
            merchant: String(t.merchant || '').trim(),
            amount: Number(t.amount ?? 0),
            wtmNumber: '',
            monthKey,
          })).filter((t) => t.merchant && Number.isFinite(t.amount));

          if (newTransactions.length) {
            await saveTransactionsToSupabase(newTransactions);
            setTransactions((prev) => [...prev.filter((item) => item.monthKey !== monthKey), ...newTransactions].sort((a, b) => compareAsc(parseISO(safeIsoDate(a.date)), parseISO(safeIsoDate(b.date)))));
          }
        }
      } catch (parseError) {
        console.error('Statement parsing failed after upload:', parseError);
      }

      setSelectedMonth(monthKey);
      setUploadNotice('Statement PDF uploaded successfully.');
    } catch (error) {
      console.error('Statement upload failed:', error);
      setStatementError(error instanceof Error ? error.message : 'Statement upload failed.');
    } finally {
      setIsImportingStatement(false);
      if (statementInputRef.current) statementInputRef.current.value = '';
    }
  };

  const updateReceipt = async (id: string, updates: Partial<Receipt>) => {
    let updatedReceipt: Receipt | undefined;
    setReceipts((prev) => prev.map((r) => {
      if (r.id === id) {
        const nextDate = safeIsoDate(updates.date ?? r.date);
        updatedReceipt = { ...r, ...updates, date: nextDate, monthKey: monthKeyForDate(nextDate) };
        return updatedReceipt;
      }
      return r;
    }));
    if (updatedReceipt) {
      await saveReceiptToSupabase(updatedReceipt);
      if (updates.date) setSelectedMonth(updatedReceipt.monthKey || monthKeyForDate(updatedReceipt.date));
    }
  };

  const updateReceiptWTM = async (id: string, wtn: string) => {
    await updateReceipt(id, { wtmNumber: wtn });
  };

  const updateTransaction = async (id: string, updates: Partial<Transaction>) => {
    let updatedTransaction: Transaction | undefined;
    setTransactions((prev) => prev.map((t) => {
      if (t.id === id) {
        updatedTransaction = { ...t, ...updates };
        return updatedTransaction;
      }
      return t;
    }));
    if (supabase && session?.user && updatedTransaction) await saveTransactionsToSupabase([updatedTransaction]);
  };

  const updateTransactionWTM = async (id: string, wtn: string) => {
    await updateTransaction(id, { wtmNumber: wtn });
  };

  const confirmDeleteReceipt = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setReceipts((prev) => prev.filter((r) => r.id !== id));
    if (supabase && session?.user) await supabase.from('receipts').delete().eq('id', id).eq('user_id', session.user.id);
    setDeleteTarget(null);
  };

  const confirmDeleteStatement = async () => {
    if (!statementDeleteTarget) return;
    const id = statementDeleteTarget.id;
    if (supabase && session?.user) await supabase.from('statements').delete().eq('id', id).eq('user_id', session.user.id);
    setStatementFiles((prev) => prev.filter((item) => item.id !== id));
    setStatementDeleteTarget(null);
  };

  const reorderReceipts = async (receiptId: string, direction: 'up' | 'down') => {
    const monthReceipts = sortedReceipts;
    const fromIndex = monthReceipts.findIndex((receipt) => receipt.id === receiptId);
    if (fromIndex < 0) return;
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= monthReceipts.length) return;

    const nextMonthReceipts = [...monthReceipts];
    const [moved] = nextMonthReceipts.splice(fromIndex, 1);
    nextMonthReceipts.splice(toIndex, 0, moved);
    const reindexed = nextMonthReceipts.map((receipt, index) => ({ ...receipt, orderIndex: index }));

    setReceipts((prev) => prev.map((receipt) => {
      const updated = reindexed.find((item) => item.id === receipt.id);
      return updated ? updated : receipt;
    }));

    await Promise.all(reindexed.map((receipt) => saveReceiptToSupabase(receipt)));
  };

  const monthKeys = useMemo(() => Array.from(new Set(receipts.map((r) => r.monthKey || monthKeyForDate(r.date)).concat(transactions.map((t) => t.monthKey || monthKeyForDate(t.date))).concat(statementFiles.map((s) => s.monthKey)))).sort(), [receipts, transactions, statementFiles]);
  const resolvedSelectedMonth = monthKeys.includes(selectedMonth) ? selectedMonth : monthKeys[0] || format(new Date(), 'yyyy-MM');
  const monthIndex = monthKeys.indexOf(resolvedSelectedMonth);
  const canGoPrevMonth = monthIndex > 0;
  const canGoNextMonth = monthIndex >= 0 && monthIndex < monthKeys.length - 1;
  const visibleReceipts = useMemo(() => receipts.filter((r) => (r.monthKey || monthKeyForDate(r.date)) === resolvedSelectedMonth), [receipts, resolvedSelectedMonth]);
  const visibleTransactions = useMemo(() => transactions.filter((t) => (t.monthKey || monthKeyForDate(t.date)) === resolvedSelectedMonth), [transactions, resolvedSelectedMonth]);
  const visibleStatementFile = useMemo(() => statementFiles.find((s) => s.monthKey === resolvedSelectedMonth), [statementFiles, resolvedSelectedMonth]);
  const sortedReceipts = useMemo(() => [...visibleReceipts].sort((a, b) => {
    const orderDiff = (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
    if (orderDiff !== 0) return orderDiff;
    return compareAsc(parseISO(safeIsoDate(a.date)), parseISO(safeIsoDate(b.date)));
  }), [visibleReceipts]);
  const filteredReceipts = useMemo(() => sortedReceipts.filter((r) => r.storeName.toLowerCase().includes(searchQuery.toLowerCase()) || r.wtmNumber.includes(searchQuery)), [sortedReceipts, searchQuery]);
  const missingWTMCount = visibleReceipts.filter((r) => !r.wtmNumber).length;
  const availableWTNs = visibleReceipts.map((r) => r.wtmNumber).filter(Boolean);
  const assignedTransactionCount = visibleTransactions.filter((t) => Boolean(t.wtmNumber)).length;
  const statementOverlayTexts = new Set((visibleStatementFile?.overlays || []).map((overlay) => String(overlay.text || '').trim()).filter(Boolean));
  const receiptsMissingRequiredFields = visibleReceipts.filter((receipt) => !receipt.storeName?.trim() || !receipt.date || !Number.isFinite(Number(receipt.amount)) || Number(receipt.amount) <= 0 || !receipt.wtmNumber?.trim());
  const visibleReceiptWTNs = Array.from(new Set(visibleReceipts.map((receipt) => receipt.wtmNumber?.trim()).filter(Boolean)));
  const unplacedReceiptWTNs = visibleReceiptWTNs.filter((wtn) => !statementOverlayTexts.has(wtn));
  const uploadReceiptsComplete = visibleReceipts.length > 0;
  const reviewReceiptsComplete = receiptsMissingRequiredFields.length === 0 && visibleReceipts.length > 0;
  const uploadStatementComplete = Boolean(visibleStatementFile);
  const labelPdfComplete = uploadStatementComplete && unplacedReceiptWTNs.length === 0;
  const packetReady = uploadReceiptsComplete && reviewReceiptsComplete && uploadStatementComplete && labelPdfComplete;

  const downloadReceiptPdf = async (receipt: Receipt) => {
    try {
      const response = await fetch('/.netlify/functions/export-package-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ receipts: [receipt], statementFile: null }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Receipt PDF download failed.');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const amountLabel = `$${Number(receipt.amount || 0).toFixed(2)}`;
      const merchantLabel = (receipt.storeName || 'Unknown Merchant').replace(/[\\/:*?"<>|]/g, '').trim();
      link.download = `${amountLabel} ${merchantLabel} Receipt.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Receipt PDF download failed:', error);
      window.alert('Receipt PDF download failed. Please try again.');
    }
  };

  const submitPacketExport = () => {
    try {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/.netlify/functions/export-package-pdf';
      form.target = '_blank';
      form.style.display = 'none';
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'payload';
      input.value = JSON.stringify({ receipts: filteredReceipts, statementFile: visibleStatementFile || null });
      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
      form.remove();
    } catch (error) {
      console.error('PDF export failed:', error);
      window.alert('PDF export failed. Please try again.');
    }
  };

  const exportPackagePdf = () => {
    if (!billingStatus?.hasSubscription) {
      setUploadError('An active subscription is required to generate a packet.');
      return;
    }

    const warnings: string[] = [];
    if (!uploadReceiptsComplete) warnings.push('No receipts have been uploaded.');
    if (receiptsMissingRequiredFields.length > 0) warnings.push(`${receiptsMissingRequiredFields.length} receipt${receiptsMissingRequiredFields.length === 1 ? ' is' : 's are'} missing required fields.`);
    if (!uploadStatementComplete) warnings.push('No statement PDF has been uploaded.');
    if (unplacedReceiptWTNs.length > 0) warnings.push(`${unplacedReceiptWTNs.length} receipt WTN${unplacedReceiptWTNs.length === 1 ? ' has' : 's have'} not been placed on the statement PDF.`);

    if (warnings.length > 0) {
      setGenerateWarnings(warnings);
      return;
    }

    submitPacketExport();
  };

  if (isAuthLoading) {
    return <div className="min-h-screen bg-[linear-gradient(180deg,#f4f7fb_0%,#eef3f8_100%)] text-slate-900 flex items-center justify-center">Loading...</div>;
  }

  if (isSupabaseConfigured && !session) {
    return <AuthScreen />;
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f4f7fb_0%,#eef3f8_100%)] text-slate-900">
      <header className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/75 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-6">
          <div className="min-w-0 flex flex-1 items-center">
            <div className="h-14 w-[210px] shrink-0 overflow-hidden sm:h-16 sm:w-[250px] md:h-20 md:w-[320px]">
              <img src="/receipta-logo-final.jpg" alt="Receipta" className="h-full w-full scale-125 object-cover object-center" />
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            {!billingStatus?.hasSubscription && !isBillingLoading && (
              <button
                onClick={async () => {
                  try {
                    setIsCheckoutLoading(true);
                    await startCheckout(import.meta.env.VITE_STRIPE_MONTHLY_PRICE_ID || '');
                  } catch (error) {
                    console.error('Stripe checkout failed:', error);
                    setUploadError(error instanceof Error ? error.message : 'Stripe checkout failed.');
                  } finally {
                    setIsCheckoutLoading(false);
                  }
                }}
                disabled={!import.meta.env.VITE_STRIPE_MONTHLY_PRICE_ID || isCheckoutLoading}
                className="inline-flex items-center gap-2 rounded-2xl border border-amber-300/70 bg-amber-50 px-4 py-2.5 text-sm font-black text-amber-900 shadow-sm transition hover:bg-amber-100 disabled:opacity-50"
              >
                {isCheckoutLoading ? 'Opening Stripe...' : 'Subscribe to Complete Packet'}
              </button>
            )}
            <button onClick={() => isMobileCamera ? setShowUploadOptions((v) => !v) : fileInputRef.current?.click()} className="inline-flex items-center gap-2 rounded-2xl bg-emerald-500 px-4 py-2.5 text-sm font-black text-slate-950 shadow-[0_12px_30px_rgba(16,185,129,0.28)] hover:bg-emerald-400">
              <Plus className="h-4 w-4" /> Upload Receipt
            </button>
            <input type="file" ref={fileInputRef} onChange={handleFileUpload} multiple accept="image/*" className="hidden" />
          </div>
        </div>
        {showUploadOptions && isMobileCamera && (
          <div className="mx-auto max-w-7xl px-4 pb-3 md:px-6">
            <div className="grid gap-2 rounded-2xl border border-slate-200 bg-white p-2 md:w-80">
              <button disabled={isUploading} onClick={() => { setShowUploadOptions(false); fileInputRef.current?.click(); }} className="flex items-center gap-2 rounded-xl px-4 py-3 text-left font-bold hover:bg-white disabled:opacity-50"><ImageIcon className="h-4 w-4" />Choose File</button>
              <button onClick={() => { setShowUploadOptions(false); setShowCameraOverlay(true); }} className="flex items-center gap-2 rounded-xl px-4 py-3 text-left font-bold hover:bg-white"><Camera className="h-4 w-4" />Use Camera</button>
            </div>
          </div>
        )}
      </header>

      <main className="mx-auto w-full max-w-7xl px-3 py-4 sm:px-4 md:px-6 md:py-8">
        <div className="mb-5 flex flex-col gap-4 md:mb-6 md:flex-row md:items-center md:justify-between">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-2">
            <MetricCard icon={<ReceiptIcon className="h-5 w-5" />} label="Receipts this month" value={visibleReceipts.length} />
            <MetricCard icon={missingWTMCount > 0 ? <AlertCircle className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />} label="Missing WTN" value={missingWTMCount} accent={missingWTMCount > 0 ? 'amber' : 'emerald'} />
          </div>
        </div>

        <div className="mb-5 flex flex-col gap-3 md:mb-6 md:flex-row md:items-center md:justify-between">
          <div className="flex w-full flex-col gap-3 md:flex-row md:items-center">
            <div className="flex items-center justify-between gap-3 rounded-[28px] border border-slate-200 bg-white p-3 md:min-w-[280px]">
              <button disabled={!canGoPrevMonth} onClick={() => canGoPrevMonth && setSelectedMonth(monthKeys[monthIndex - 1])} className="rounded-xl p-2 text-slate-600 disabled:opacity-30 hover:bg-white"><ChevronLeft className="h-4 w-4" /></button>
              <div className="min-w-0 flex-1 text-center text-sm font-bold">{monthKeys.length ? toMonthLabel(resolvedSelectedMonth) : toMonthLabel(format(new Date(), 'yyyy-MM'))}</div>
              <button disabled={!canGoNextMonth} onClick={() => canGoNextMonth && setSelectedMonth(monthKeys[monthIndex + 1])} className="rounded-xl p-2 text-slate-600 disabled:opacity-30 hover:bg-white"><ChevronRight className="h-4 w-4" /></button>
            </div>
            <div className="relative w-full md:max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search merchant or WTN" className="w-full rounded-2xl border border-slate-200 bg-white/90 py-3 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-emerald-400 focus:outline-none" />
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div className="flex items-center gap-2">
              {viewMode !== 'dashboard' && <button onClick={() => setViewMode('dashboard')} className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white/85 px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 shadow-sm"><ChevronLeft className="h-4 w-4" />Back</button>}
              {viewMode !== 'dashboard' && <button onClick={() => setViewMode(viewMode === 'package' ? 'dashboard' : 'package')} className="inline-flex items-center gap-2 rounded-2xl bg-emerald-500 px-4 py-3 text-sm font-black text-slate-950 shadow-[0_12px_30px_rgba(16,185,129,0.28)] hover:bg-emerald-400"><FileText className="h-4 w-4" />Generate Packet</button>}
            </div>
          </div>
        </div>

        {isUploading && <div className="mb-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm font-medium text-emerald-100">Uploading receipt...</div>}
        {uploadNotice && <div className="mb-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm font-medium text-slate-900">{uploadNotice}</div>}
        {!!debugSteps.length && <div className="mb-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600">{debugSteps.map((step, index) => <div key={`${step}-${index}`}>{step}</div>)}</div>}
        {uploadError && <Banner tone="error" message={uploadError} />}
        {statementError && <Banner tone="error" message={statementError} />}

        <GuidedFlowPanel
          viewMode={viewMode}
          uploadReceiptsComplete={uploadReceiptsComplete}
          reviewReceiptsComplete={reviewReceiptsComplete}
          uploadStatementComplete={uploadStatementComplete}
          labelPdfComplete={labelPdfComplete}
          packetReady={packetReady}
          missingReceiptFieldsCount={receiptsMissingRequiredFields.length}
          unplacedReceiptWTNsCount={unplacedReceiptWTNs.length}
          onOpenReceipts={() => setViewMode('dashboard')}
          onOpenStatement={() => setViewMode('dashboard')}
          onOpenLabelPdf={() => setViewMode('statement-label')}
          onOpenGenerate={() => setViewMode('package')}
          onGenerateNow={exportPackagePdf}
        />

        {viewMode === 'dashboard' ? (
          <section className="space-y-4 lg:space-y-6">
            <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white/80 p-3 sm:p-4 md:rounded-[28px] md:p-5">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-lg font-black">Receipts</h2>
                  <p className="text-sm text-slate-500">Download, edit, reconcile, and move receipts up or down to set the final package order for this month.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button disabled={isUploading} onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                    <Plus className="h-4 w-4" /> {isUploading ? 'Uploading...' : 'Upload Receipt'}
                  </button>
                </div>
              </div>
              {filteredReceipts.length ? (
                <div className="space-y-2.5">
                  {filteredReceipts.map((receipt) => (
                    <div key={receipt.id} className="space-y-1.5">
                      <ReceiptCard receipt={receipt} updateReceipt={updateReceipt} updateReceiptWTM={updateReceiptWTM} requestDelete={() => setDeleteTarget(receipt)} onDownload={() => downloadReceiptPdf(receipt)} onPreview={() => setPreviewReceipt(receipt)} />
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState title="No receipts for this month" description="Upload a receipt to start the monthly packet." />
              )}
            </div>

            <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white/80 p-3 sm:p-4 md:rounded-[28px] md:p-5">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-lg font-black">Statement upload</h2>
                  <p className="text-sm text-slate-500">Upload or replace your statement PDF. Review and labeling happen in the next steps.</p>
                </div>
                <label className={cn('inline-flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 shadow-sm', isImportingStatement && 'opacity-60')}>
                  <Plus className="h-4 w-4" /> {isImportingStatement ? 'Uploading...' : visibleStatementFile ? 'Replace Statement PDF' : 'Upload Statement PDF'}
                  <input type="file" ref={statementInputRef} onChange={handleStatementUpload} accept=".pdf,application/pdf,application/x-pdf" className="hidden" />
                </label>
              </div>
              {visibleStatementFile ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-xs font-black uppercase tracking-[0.24em] text-slate-400">Statement PDF</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{visibleStatementFile.fileName}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <a href={visibleStatementFile.fileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 shadow-sm">
                      <FileText className="h-4 w-4" /> Open PDF
                    </a>
                    <a href={visibleStatementFile.fileUrl} download={visibleStatementFile.fileName} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 shadow-sm">
                      <Download className="h-4 w-4" /> Download PDF
                    </a>
                    <button onClick={() => setStatementDeleteTarget(visibleStatementFile)} className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-bold text-rose-700 hover:bg-rose-100 shadow-sm">
                      <Trash2 className="h-4 w-4" /> Delete Statement
                    </button>
                  </div>
                </div>
              ) : (
                <EmptyState title="No statement uploaded" description="Upload the exact PDF statement for this month." />
              )}
            </div>
          </section>
        ) : viewMode === 'statement-label' ? (
          <section className="space-y-4 lg:space-y-6">
            <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white/80 p-3 sm:p-4 md:rounded-[28px] md:p-5">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-black">Statement label placement</h2>
                  <p className="text-sm text-slate-500">Place freeform labels on the uploaded statement PDF. Transaction rows remain the source of truth.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => statementEditorRef.current?.undoLastPlacement()}
                    disabled={!canUndoStatementLabel}
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Undo2 className="h-4 w-4" /> Undo
                  </button>
                  <button onClick={() => setViewMode('package')} className="inline-flex items-center gap-2 rounded-2xl bg-emerald-500 px-4 py-3 text-sm font-black text-slate-950 shadow-[0_12px_30px_rgba(16,185,129,0.28)] hover:bg-emerald-400"><FileText className="h-4 w-4" />Continue to Packet</button>
                </div>
              </div>
              {visibleStatementFile ? (
                <StatementPdfEditor
                  ref={statementEditorRef}
                  statementFile={visibleStatementFile}
                  receipts={visibleReceipts}
                  availableWTNs={availableWTNs}
                  onUndoAvailabilityChange={setCanUndoStatementLabel}
                  onSave={async (overlays: StatementOverlay[]) => {
                    if (!visibleStatementFile) return;
                    const updated = { ...visibleStatementFile, overlays };
                    await saveStatementFileToSupabase(updated);
                    setStatementFiles((prev) => prev.map((item) => item.id === updated.id ? updated : item));
                    setUploadNotice('Statement labels saved.');
                  }}
                />
              ) : (
                <EmptyState title="No statement uploaded" description="Upload the exact PDF statement for this month." />
              )}
            </div>
          </section>
        ) : (
          <PacketView receipts={sortedReceipts} transactions={visibleTransactions} statementFile={visibleStatementFile} onDownloadReceipt={downloadDataUrl} onDownloadReceiptSheet={downloadReceiptPdf} onExportPdf={exportPackagePdf} updateTransactionWTM={updateTransactionWTM} availableWTNs={availableWTNs} onReorderReceipt={reorderReceipts} />
        )}
      </main>

      <BottomMenu showMenu={showMenu} setShowMenu={setShowMenu} session={session} billingStatus={billingStatus} onManageBilling={async () => { try { setIsCheckoutLoading(true); await openCustomerPortal(); } catch (error) { console.error('Stripe portal failed:', error); setUploadError(error instanceof Error ? error.message : 'Could not open subscription portal.'); } finally { setIsCheckoutLoading(false); } }} onSignOut={async () => { if (!supabase) return; await supabase.auth.signOut(); setReceipts([]); setTransactions([]); setBillingStatus(null); }} />

      <AnimatePresence>
        {previewReceipt && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} className="relative w-full max-w-4xl rounded-[28px] border border-slate-200 bg-white p-4 shadow-2xl">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-black text-slate-900">{previewReceipt.storeName}</h3>
                  <p className="text-sm text-slate-500">Raw receipt image</p>
                </div>
                <button onClick={() => setPreviewReceipt(null)} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50">
                  <X className="h-4 w-4" /> Close
                </button>
              </div>
              <div className="max-h-[80vh] overflow-auto rounded-2xl border border-slate-200 bg-slate-50 p-3">
                {previewReceipt.imageUrl ? <img src={previewReceipt.imageUrl} alt={previewReceipt.storeName} className="mx-auto h-auto max-w-full rounded-xl" referrerPolicy="no-referrer" /> : <div className="flex min-h-60 items-center justify-center text-sm font-bold text-slate-400">No receipt image uploaded</div>}
              </div>
            </motion.div>
          </motion.div>
        )}
        {deleteTarget && (
          <ConfirmModal title="Delete receipt?" description={`This will remove ${deleteTarget.storeName} from ${toMonthLabel(deleteTarget.monthKey || monthKeyForDate(deleteTarget.date))}.`} confirmLabel="Delete receipt" onCancel={() => setDeleteTarget(null)} onConfirm={() => void confirmDeleteReceipt()} />
        )}
        {statementDeleteTarget && (
          <ConfirmModal title="Delete statement?" description={`This will remove ${statementDeleteTarget.fileName} from ${toMonthLabel(statementDeleteTarget.monthKey)}.`} confirmLabel="Delete statement" onCancel={() => setStatementDeleteTarget(null)} onConfirm={() => void confirmDeleteStatement()} />
        )}
        {generateWarnings && (
          <ConfirmModal title="Generate packet with missing items?" description={generateWarnings.join('\n')} confirmLabel="Generate Anyway" onCancel={() => setGenerateWarnings(null)} onConfirm={() => { setGenerateWarnings(null); submitPacketExport(); }} />
        )}
      </AnimatePresence>

      {capturedImage && (
        <div className="fixed inset-0 z-50 flex flex-col bg-slate-100/95 text-slate-900">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.3em] text-slate-500">Review Capture</p>
              <p className="text-sm text-slate-600">This version saves the full photo instead of perspective-cropping it, so make sure the receipt is framed cleanly.</p>
            </div>
            <button onClick={() => setCapturedImage(null)} className="rounded-full bg-white/10 p-2 hover:bg-white/20"><X className="h-5 w-5" /></button>
          </div>
          <div className="flex flex-1 items-center justify-center overflow-auto bg-slate-100 px-4 py-6" style={{ WebkitOverflowScrolling: 'touch' }}>
            <img src={capturedImage} alt="Receipt capture preview" className="max-h-full w-full max-w-3xl rounded-[28px] border border-slate-200 object-contain shadow-2xl shadow-black/40" />
          </div>
          <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
            <button onClick={() => { setCapturedImage(null); setShowCameraOverlay(true); }} className="flex-1 rounded-2xl border border-slate-200 px-4 py-3 font-bold text-slate-800">Retake</button>
            <button onClick={() => void finalizeManualCrop()} disabled={isUploading} className="flex-[1.3] rounded-2xl bg-emerald-500 px-4 py-3 font-black text-slate-950 disabled:opacity-50">{isUploading ? 'Saving...' : 'Use Photo'}</button>
          </div>
        </div>
      )}

      {showCameraOverlay && (
        <div className="fixed inset-0 z-50 flex flex-col bg-[linear-gradient(180deg,#f4f7fb_0%,#eef3f8_100%)] text-slate-900">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.3em] text-slate-500">Camera Scan</p>
              <p className="text-sm text-slate-600">Capture the full receipt photo, then crop it.</p>
            </div>
            <button onClick={() => setShowCameraOverlay(false)} className="rounded-full bg-white/10 p-2 hover:bg-white/20"><X className="h-5 w-5" /></button>
          </div>
          <div className="relative flex-1 overflow-hidden bg-slate-100">
            <video ref={videoRef} playsInline muted onCanPlay={() => setIsCameraReady(true)} className="absolute inset-0 h-full w-full object-cover" />
          </div>
          <div className="space-y-3 border-t border-slate-200 px-5 py-4">
            {!isCameraReady && <p className="text-sm text-slate-900/55">Starting camera...</p>}
            {cameraError && <p className="text-sm text-rose-300">{cameraError}</p>}
            <div className="flex gap-3">
              <button onClick={() => setShowCameraOverlay(false)} className="flex-1 rounded-2xl border border-slate-200 px-4 py-3 font-bold text-slate-800">Cancel</button>
              <button onClick={() => void handleCameraCapture()} disabled={!isCameraReady || isCaptureProcessing} className="flex-[1.3] rounded-2xl bg-emerald-500 px-4 py-3 font-black text-slate-950 disabled:opacity-50">{isCaptureProcessing ? 'Processing...' : 'Capture Photo'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon, label, value, accent = 'slate' }: { icon: React.ReactNode; label: string; value: number; accent?: 'slate' | 'emerald' | 'amber' }) {
  const tone = accent === 'emerald' ? 'bg-emerald-500/15 text-emerald-400' : accent === 'amber' ? 'bg-amber-500/15 text-amber-400' : 'bg-white/10 text-slate-900';
  return (
    <div className="rounded-3xl border border-slate-200/80 bg-white/85 p-4 shadow-[0_10px_30px_rgba(15,23,42,0.05)] backdrop-blur-sm">
      <div className="flex items-center gap-3">
        <div className={cn('rounded-2xl p-2', tone)}>{icon}</div>
        <div>
          <p className="text-xs uppercase tracking-[0.24em] text-slate-500">{label}</p>
          <p className="mt-1 text-2xl font-black">{value}</p>
        </div>
      </div>
    </div>
  );
}

function Banner({ tone, message }: { tone: 'error'; message: string }) {
  return <div className={cn('mb-4 rounded-2xl px-4 py-3 text-sm font-medium', tone === 'error' && 'bg-rose-500/10 text-rose-200 border border-rose-500/20')}>{message}</div>;
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return <div className="rounded-[28px] border border-dashed border-slate-300 bg-white/70 p-10 text-center shadow-[0_10px_30px_rgba(15,23,42,0.04)]"><p className="text-lg font-black">{title}</p><p className="mt-2 text-sm text-slate-500">{description}</p></div>;
}

function AuthScreen() {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    document.title = 'Receipta | Receipt and Statement Reconciliation for Property Management Teams';
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!supabase) return;
    setIsSubmitting(true); setError(''); setMessage('');
    try {
      if (mode === 'signup') {
        const { error: signUpError } = await supabase.auth.signUp({ email, password, options: { emailRedirectTo: undefined, data: {} } });
        if (signUpError) throw signUpError;
        setMessage('Account created. You can sign in immediately.');
        setMode('signin');
        setPassword('');
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) throw signInError;
      }
    } catch (submitError: any) {
      console.error('Authentication failed:', submitError);
      setError(submitError?.message || 'Authentication failed. Please try again.');
    } finally { setIsSubmitting(false); }
  };

  const features = [
    {
      title: 'Capture every receipt in one place',
      description: 'Upload from desktop or mobile, keep monthly records together, and stop chasing loose files across texts, inboxes, and folders.',
    },
    {
      title: 'Map receipts to WTN numbers fast',
      description: 'Assign WTN numbers cleanly, line up statement entries, and make exceptions visible before month-end gets messy.',
    },
    {
      title: 'Export audit-ready monthly packets',
      description: 'Package receipts and statement context into a format that is easier to review, send, and defend.',
    },
  ];

  const seoPoints = [
    'receipt reconciliation software for property management',
    'monthly receipt packet workflow',
    'WTN tracking and statement matching',
    'finance team audit packet preparation',
  ];

  const faqs = [
    {
      question: 'Who is Receipta for?',
      answer: 'Receipta is built for property management operators, bookkeepers, and finance teams that need to organize receipts against monthly statements without relying on bloated accounting workflows.',
    },
    {
      question: 'What problem does it solve?',
      answer: 'It reduces the friction between collecting receipts, labeling them, matching them to statement activity, and exporting a clean packet for review or audit support.',
    },
    {
      question: 'Why is this better than folders and spreadsheets?',
      answer: 'Folders store files. Receipta creates an actual reconciliation workflow with receipt metadata, WTN mapping, statement context, and packet export in one place.',
    },
  ];

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f8fafc_0%,#eef6f3_100%)] text-slate-900">
      <section className="border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-5 sm:px-6 lg:px-8">
          <div>
            <div className="h-14 w-[210px] overflow-hidden sm:h-16 sm:w-[250px] md:h-20 md:w-[320px]">
              <img src="/receipta-logo-final.jpg" alt="Receipta" className="h-full w-full scale-125 object-cover object-center" />
            </div>
            <p className="mt-1 text-[11px] uppercase tracking-[0.28em] text-slate-500">Receipt reconciliation software</p>
          </div>
          <button onClick={() => document.getElementById('auth-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="rounded-2xl bg-emerald-500 px-4 py-2.5 text-sm font-black text-slate-950 shadow-[0_12px_30px_rgba(16,185,129,0.25)] hover:bg-emerald-400">
            Start free
          </button>
        </div>
      </section>

      <main>
        <section className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[minmax(0,1.2fr)_420px] lg:px-8 lg:py-20">
          <div>
            <p className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-black uppercase tracking-[0.28em] text-emerald-700">
              Built for property management finance teams
            </p>
            <h1 className="mt-5 max-w-4xl text-4xl font-black tracking-tight text-slate-950 sm:text-5xl lg:text-6xl">
              The fastest way to turn receipt chaos into a clean monthly packet.
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-600">
              Receipta helps teams collect receipts, assign WTN numbers, reconcile statement activity, and export audit-ready packets without the usual spreadsheet mess.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button onClick={() => document.getElementById('auth-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="rounded-2xl bg-emerald-500 px-6 py-3.5 text-base font-black text-slate-950 shadow-[0_12px_30px_rgba(16,185,129,0.28)] hover:bg-emerald-400">
                Create free account
              </button>
              <div className="rounded-2xl border border-slate-200 bg-white px-5 py-3.5 text-sm font-semibold text-slate-600">
                Faster receipt reconciliation, cleaner month-end review
              </div>
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-3xl font-black text-slate-950">1</p>
                <p className="mt-2 text-sm font-bold text-slate-900">Upload receipts</p>
                <p className="mt-1 text-sm text-slate-500">Centralize receipt images from the field or office.</p>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-3xl font-black text-slate-950">2</p>
                <p className="mt-2 text-sm font-bold text-slate-900">Match by WTN</p>
                <p className="mt-1 text-sm text-slate-500">Connect receipts to statement activity without guesswork.</p>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-3xl font-black text-slate-950">3</p>
                <p className="mt-2 text-sm font-bold text-slate-900">Export packets</p>
                <p className="mt-1 text-sm text-slate-500">Produce a clean monthly packet for review, bookkeeping, or audit support.</p>
              </div>
            </div>
          </div>

          <div id="auth-card" className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_25px_80px_rgba(15,23,42,0.12)] sm:p-8 lg:sticky lg:top-8 lg:self-start">
            <p className="text-sm font-black uppercase tracking-[0.28em] text-emerald-600">Get started</p>
            <h2 className="mt-3 text-2xl font-black tracking-tight">Create your Receipta workspace</h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">Start organizing receipts, statements, and WTN mappings in one workflow.</p>

            <div className="mt-6 flex gap-2 rounded-2xl bg-slate-100 p-1">
              <button type="button" onClick={() => setMode('signin')} className={cn('flex-1 rounded-xl px-4 py-2.5 text-sm font-bold', mode === 'signin' ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500')}>
                Sign in
              </button>
              <button type="button" onClick={() => setMode('signup')} className={cn('flex-1 rounded-xl px-4 py-2.5 text-sm font-bold', mode === 'signup' ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500')}>
                Create account
              </button>
            </div>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">Email</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 shadow-sm outline-none focus:border-emerald-400" placeholder="you@example.com" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">Password</label>
                <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 shadow-sm outline-none focus:border-emerald-400" placeholder="Minimum 6 characters" />
              </div>
              {error && <Banner tone="error" message={error} />}
              {message && <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700">{message}</div>}
              <button type="submit" disabled={isSubmitting} className="w-full rounded-2xl bg-emerald-500 px-4 py-3.5 font-black text-slate-950 shadow-[0_12px_30px_rgba(16,185,129,0.2)] disabled:opacity-50">
                {isSubmitting ? 'Please wait...' : mode === 'signin' ? 'Sign in to Receipta' : 'Create free account'}
              </button>
            </form>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-6 sm:px-6 lg:px-8">
          <div className="rounded-[32px] border border-slate-200 bg-white p-8 shadow-sm">
            <div className="max-w-3xl">
              <p className="text-sm font-black uppercase tracking-[0.28em] text-slate-500">Why teams use Receipta</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950">Built around the actual reconciliation workflow</h2>
              <p className="mt-3 text-base leading-7 text-slate-600">Receipta is not another generic file bucket. It is purpose-built for the handoff between receipt collection, statement review, and monthly audit packet prep.</p>
            </div>
            <div className="mt-8 grid gap-5 lg:grid-cols-3">
              {features.map((feature) => (
                <article key={feature.title} className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
                  <h3 className="text-lg font-black text-slate-950">{feature.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{feature.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
          <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="rounded-[32px] border border-slate-200 bg-slate-950 p-8 text-white shadow-sm">
              <p className="text-sm font-black uppercase tracking-[0.28em] text-emerald-300">Search visibility focus</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight">Clear positioning around the keywords that matter</h2>
              <ul className="mt-6 space-y-3 text-sm leading-6 text-slate-300">
                {seoPoints.map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <span className="mt-1 h-2.5 w-2.5 rounded-full bg-emerald-400" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-[32px] border border-slate-200 bg-white p-8 shadow-sm">
              <p className="text-sm font-black uppercase tracking-[0.28em] text-slate-500">Common questions</p>
              <div className="mt-5 space-y-5">
                {faqs.map((faq) => (
                  <article key={faq.question}>
                    <h3 className="text-lg font-black text-slate-950">{faq.question}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600">{faq.answer}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function ReceiptCard({ receipt, updateReceipt, updateReceiptWTM, requestDelete, onDownload, onPreview, allowEdit = true, allowDelete = true, previewLabel = 'Receipt', downloadLabel = 'Receipt PDF' }: { receipt: Receipt; updateReceipt: (id: string, updates: Partial<Receipt>) => Promise<void>; updateReceiptWTM: (id: string, wtn: string) => Promise<void>; requestDelete: () => void; onDownload: () => void; onPreview: () => void; allowEdit?: boolean; allowDelete?: boolean; previewLabel?: string; downloadLabel?: string }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [merchantDraft, setMerchantDraft] = useState(receipt.storeName);
  const [amountDraft, setAmountDraft] = useState(receipt.amount.toFixed(2));
  const [dateDraft, setDateDraft] = useState(safeIsoDate(receipt.date));
  const [summaryDraft, setSummaryDraft] = useState(receipt.summary || '');
  useEffect(() => {
    setMerchantDraft(receipt.storeName);
    setAmountDraft(receipt.amount.toFixed(2));
    setDateDraft(safeIsoDate(receipt.date));
    setSummaryDraft(receipt.summary || '');
  }, [receipt.storeName, receipt.amount, receipt.date, receipt.summary]);
  const saveEdits = async () => {
    setIsSaving(true);
    try {
      const nextDate = safeIsoDate(dateDraft);
      await updateReceipt(receipt.id, {
        storeName: merchantDraft.trim() || receipt.storeName,
        amount: Number.isFinite(Number(amountDraft)) ? Number(amountDraft) : receipt.amount,
        date: nextDate,
        monthKey: monthKeyForDate(nextDate),
        summary: summaryDraft.trim(),
      });
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <motion.div layout initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="overflow-hidden rounded-[20px] sm:rounded-[24px] border border-slate-200/80 bg-white/90 shadow-[0_10px_22px_rgba(15,23,42,0.05)] backdrop-blur-sm">
      <div className="flex flex-col gap-2.5 p-3 sm:gap-3 sm:p-4">
        <div className="flex items-start gap-2.5 sm:gap-3">
          <button type="button" onClick={onPreview} className="h-16 w-12 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50 sm:h-20 sm:w-16">
            {receipt.imageUrl ? <img src={receipt.imageUrl} alt="Receipt" className="h-full w-full object-cover" referrerPolicy="no-referrer" /> : <div className="flex h-full items-center justify-center text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">No Image</div>}
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                {isEditing ? <input value={merchantDraft} onChange={(e) => setMerchantDraft(e.target.value)} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold shadow-sm sm:px-3 sm:py-2.5 sm:text-base" /> : <h3 className="truncate text-[15px] font-black leading-tight sm:text-lg">{receipt.storeName}</h3>}
                {isEditing ? <input type="date" value={dateDraft} onChange={(e) => setDateDraft(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-900 shadow-sm sm:mt-1.5 sm:px-3 sm:py-2.5 sm:text-sm" /> : <p className="mt-0.5 text-[11px] sm:text-sm text-slate-500">{format(parseISO(safeIsoDate(receipt.date)), 'MMM dd, yyyy')}</p>}
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                {allowEdit && <button disabled={isSaving} onClick={() => isEditing ? void saveEdits() : setIsEditing(true)} className={isEditing ? "inline-flex items-center gap-1 rounded-lg bg-emerald-500 p-1.5 text-slate-950 shadow-sm hover:bg-emerald-400 touch-manipulation disabled:opacity-60 sm:rounded-xl sm:p-2" : "inline-flex items-center gap-1 rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-100 touch-manipulation disabled:opacity-60 sm:rounded-xl sm:p-2"} aria-label={isEditing ? 'Save receipt changes' : 'Edit receipt'} title={isEditing ? 'Save receipt changes' : 'Edit receipt'}>{isEditing ? <><Save className="h-3.5 w-3.5 sm:h-4 sm:w-4" /><span className="hidden sm:inline text-sm font-bold">{isSaving ? 'Saving...' : 'Save'}</span></> : <Pencil className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}</button>}
                <button onClick={onDownload} className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-100 touch-manipulation sm:rounded-xl sm:p-2" title={downloadLabel} aria-label={downloadLabel}><Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" /></button>
                {allowDelete && <button onClick={requestDelete} className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-rose-50 hover:text-rose-600 sm:rounded-xl sm:p-2"><Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" /></button>}
              </div>
            </div>
            <div className="mt-1.5 flex flex-col gap-1.5 sm:mt-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
              {isEditing ? <input type="number" step="0.01" value={amountDraft} onChange={(e) => setAmountDraft(e.target.value)} className="w-full sm:w-36 rounded-xl border border-slate-200 bg-white/95 px-3 py-2 text-sm font-black shadow-sm sm:py-2.5 sm:text-base" /> : <span className="text-base font-black sm:text-lg">${receipt.amount.toFixed(2)}</span>}
              {isEditing ? (
                <div className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white/95 px-3 py-2 shadow-sm sm:py-2.5">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className="text-[10px] sm:text-xs font-black uppercase tracking-[0.18em] sm:tracking-[0.2em] text-slate-400">WTN</span>
                    <input value={receipt.wtmNumber} onChange={(e) => void updateReceiptWTM(receipt.id, e.target.value)} placeholder="Enter WTN" className="min-w-0 flex-1 bg-transparent text-sm font-mono outline-none sm:text-base" />
                  </div>
                </div>
              ) : (
                <div className="min-w-0 flex-1 px-0.5 py-0.5">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className="text-[10px] sm:text-xs font-black uppercase tracking-[0.18em] sm:tracking-[0.2em] text-slate-400">WTN</span>
                    <span className={cn("min-w-0 flex-1 font-mono text-sm sm:text-base", receipt.wtmNumber ? "text-slate-900" : "rounded-md bg-rose-50 px-2 py-0.5 text-rose-400")}>{receipt.wtmNumber || 'MISSING'}</span>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
        <div className="rounded-xl bg-slate-50 px-3 py-2 sm:px-3.5 sm:py-2.5">
          {isEditing ? <textarea value={summaryDraft} onChange={(e) => setSummaryDraft(e.target.value)} rows={3} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm" /> : <p className="text-xs italic leading-snug text-slate-600 sm:text-sm">“{receipt.summary}”</p>}
        </div>
      </div>
    </motion.div>
  );
}

const StatementPdfEditor = React.forwardRef<StatementPdfEditorHandle, { statementFile: StatementFile; receipts: Receipt[]; availableWTNs: string[]; onSave: (overlays: StatementOverlay[]) => Promise<void>; onUndoAvailabilityChange?: (canUndo: boolean) => void }>(function StatementPdfEditor({ statementFile, receipts, availableWTNs, onSave, onUndoAvailabilityChange }, ref) {
  const [pageImages, setPageImages] = useState<string[]>([]);
  const overlaysRef = useRef<StatementOverlay[]>(statementFile.overlays || []);
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  const pendingSaveCountRef = useRef(0);
  const [activeTool, setActiveTool] = useState<'wtn' | 'signature' | 'printed_name' | 'date'>('wtn');
  const [selectedWTN, setSelectedWTN] = useState('');
  const [signatureName, setSignatureName] = useState('');
  const [printedName, setPrintedName] = useState('');
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [overlays, setOverlays] = useState<StatementOverlay[]>(statementFile.overlays || []);
  const [hoverPreview, setHoverPreview] = useState<{ page: number; x: number; y: number } | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [draggingOverlayId, setDraggingOverlayId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    const nextOverlays = statementFile.overlays || [];
    overlaysRef.current = nextOverlays;
    setOverlays(nextOverlays);
    setDraggingOverlayId(null);
  }, [statementFile]);

  useEffect(() => {
    overlaysRef.current = overlays;
  }, [overlays]);

  useEffect(() => {
    onUndoAvailabilityChange?.(overlays.length > 0);
  }, [onUndoAvailabilityChange, overlays.length]);

  useEffect(() => {
    setIsTouchDevice(typeof window !== 'undefined' && (window.matchMedia?.('(pointer: coarse)').matches || 'ontouchstart' in window));
  }, []);

  useEffect(() => {
    const renderPdf = async () => {
      if (!statementFile.fileUrl?.startsWith('data:application/pdf')) {
        setPageImages([]);
        return;
      }
      const pdf = await pdfjsLib.getDocument(statementFile.fileUrl).promise;
      const images: string[] = [];
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.35 });
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: ctx, viewport, canvas }).promise;
        images.push(canvas.toDataURL('image/png'));
      }
      setPageImages(images);
    };

    void renderPdf();
  }, [statementFile.fileUrl]);

  const activePlacement = useMemo(() => {
    if (activeTool === 'wtn') return selectedWTN ? { type: 'wtn' as const, text: selectedWTN, fontSize: 11.2 } : null;
    if (activeTool === 'signature') return signatureName.trim() ? { type: 'signature' as const, text: signatureName.trim(), fontSize: 15.5 } : null;
    if (activeTool === 'printed_name') return printedName.trim() ? { type: 'printed_name' as const, text: printedName.trim(), fontSize: 9.6 } : null;
    return { type: 'date' as const, text: toDisplayDate(selectedDate), fontSize: 9.6 };
  }, [activeTool, printedName, selectedDate, selectedWTN, signatureName]);

  const queueOverlaySave = (nextOverlays: StatementOverlay[], successMessage: string) => {
    pendingSaveCountRef.current += 1;
    setIsSaving(true);
    setSaveMessage('Saving...');
    const snapshot = nextOverlays.map((overlay) => ({ ...overlay }));
    const runSave = async () => {
      try {
        await onSave(snapshot);
        setSaveMessage(successMessage);
      } catch (error) {
        setSaveMessage(error instanceof Error ? error.message : 'Failed to save labels.');
      } finally {
        pendingSaveCountRef.current = Math.max(0, pendingSaveCountRef.current - 1);
        if (pendingSaveCountRef.current === 0) setIsSaving(false);
      }
    };
    const queuedSave = saveChainRef.current.then(runSave, runSave);
    saveChainRef.current = queuedSave.catch(() => {});
  };

  const updateOverlays = (nextOverlays: StatementOverlay[], successMessage?: string) => {
    overlaysRef.current = nextOverlays;
    setOverlays(nextOverlays);
    if (successMessage) queueOverlaySave(nextOverlays, successMessage);
  };

  const undoLastPlacement = () => {
    if (!overlaysRef.current.length) return;
    updateOverlays(overlaysRef.current.slice(0, -1), 'Undo saved.');
  };

  React.useImperativeHandle(ref, () => ({
    undoLastPlacement,
  }), []);

  const addOverlayPlacement = (page: number, type: NonNullable<StatementOverlay['type']>, text: string, fontSize: number, x: number, y: number) => {
    updateOverlays([...overlaysRef.current, { id: makeId(), type, text, page, x, y, fontSize }], 'Label placed and saved.');
  };

  const placeOverlayAtPoint = (page: number, clientX: number, clientY: number, container: HTMLDivElement) => {
    if (!activePlacement) return;
    const rect = container.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    addOverlayPlacement(page, activePlacement.type, activePlacement.text, activePlacement.fontSize, x, y);
  };

  return (
    <div className="grid gap-4 xl:grid-cols-[360px_minmax(0,1fr)] xl:items-start">
      <div className="space-y-4 xl:sticky xl:top-24">
        <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'wtn', label: 'WTN' },
              { key: 'signature', label: 'Signature' },
              { key: 'printed_name', label: 'Name' },
              { key: 'date', label: 'Date' },
            ].map((tool) => (
              <button
                key={tool.key}
                onClick={() => setActiveTool(tool.key as 'wtn' | 'signature' | 'printed_name' | 'date')}
                className={cn('rounded-xl px-4 py-2 text-sm font-bold', activeTool === tool.key ? 'bg-slate-900 text-white' : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50')}
              >
                {tool.label}
              </button>
            ))}
          </div>

          {activeTool === 'wtn' ? (
            availableWTNs.length ? (
              <div className="overflow-hidden rounded-2xl border border-slate-200">
                <div className="grid grid-cols-[minmax(0,1fr)_120px] bg-slate-50 px-4 py-3 text-xs font-black uppercase tracking-[0.2em] text-slate-500">
                  <div>WTN</div>
                  <div>Total</div>
                </div>
                <div className="divide-y divide-slate-200">
                  {availableWTNs.map((wtn) => {
                    const total = receipts.filter((receipt) => receipt.wtmNumber === wtn).reduce((sum, receipt) => sum + Number(receipt.amount || 0), 0);
                    return (
                      <button key={wtn} onClick={() => setSelectedWTN(wtn)} className={cn('grid w-full grid-cols-[minmax(0,1fr)_120px] px-4 py-3 text-left text-sm', selectedWTN === wtn ? 'bg-amber-100 text-slate-900' : 'bg-white text-slate-700 hover:bg-slate-50')}>
                        <div className="font-mono font-bold">{wtn}</div>
                        <div className="font-semibold">${total.toFixed(2)}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : <p className="text-sm text-slate-500">Upload receipts with WTNs first, then place them on the statement.</p>
          ) : null}

          {activeTool === 'signature' ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <label className="mb-2 block text-sm font-bold text-slate-700">Signature name</label>
              <input value={signatureName} onChange={(e) => setSignatureName(e.target.value)} placeholder="Enter signature name" className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-900 shadow-sm" />
            </div>
          ) : null}

          {activeTool === 'printed_name' ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <label className="mb-2 block text-sm font-bold text-slate-700">Printed name</label>
              <input value={printedName} onChange={(e) => setPrintedName(e.target.value)} placeholder="Enter printed name" className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-900 shadow-sm" />
            </div>
          ) : null}

          {activeTool === 'date' ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <label className="mb-2 block text-sm font-bold text-slate-700">Date</label>
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-900 shadow-sm" />
              <p className="mt-2 text-xs text-slate-500">Placed as {toDisplayDate(selectedDate)}</p>
            </div>
          ) : null}

          <div className="space-y-2 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-xs text-slate-500">Select a value on the left, then click the PDF to place it. Double-click any placed label to delete it.</p>
            {activePlacement ? <span className="inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-900">Placement mode: {activePlacement.text}</span> : null}
            <p className="text-xs text-slate-500">Labels stay active after placement, so you can place the same one more than once. Drag any placed label to reposition it.</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
            {isSaving ? 'Saving changes...' : 'Changes save automatically after each placement, move, delete, and undo.'}
          </div>
        </div>

        {saveMessage ? <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-900">{saveMessage}</div> : null}
      </div>

      <div className="space-y-4">
        {pageImages.map((src, index) => (
          <div key={index} className="rounded-3xl border border-slate-200 bg-white p-3 shadow-sm">
            <div
              className={cn('relative', activePlacement && 'cursor-crosshair')}
              onMouseMove={(event) => {
                if (!activePlacement) return;
                const rect = event.currentTarget.getBoundingClientRect();
                const x = ((event.clientX - rect.left) / rect.width) * 100;
                const y = ((event.clientY - rect.top) / rect.height) * 100;
                setHoverPreview({ page: index, x, y });
              }}
              onMouseLeave={() => setHoverPreview((prev) => prev?.page === index ? null : prev)}
              onClick={(event) => {
                if (!activePlacement || isTouchDevice) return;
                placeOverlayAtPoint(index, event.clientX, event.clientY, event.currentTarget);
              }}
              onPointerUp={(event) => {
                if (!activePlacement || !isTouchDevice) return;
                if (draggingOverlayId) return;
                placeOverlayAtPoint(index, event.clientX, event.clientY, event.currentTarget);
              }}
            >
              <img src={src} alt={`Statement page ${index + 1}`} className="w-full rounded-2xl border border-slate-200" />
              {hoverPreview?.page === index && activePlacement && !isTouchDevice ? (
                <div
                  className={cn('pointer-events-none absolute z-10 text-slate-900 opacity-35', activePlacement.type === 'wtn' ? 'font-mono font-bold' : 'bg-transparent shadow-none', activePlacement.type === 'signature' ? 'font-serif italic text-blue-900' : 'font-medium')}
                  style={{ left: `${hoverPreview.x}%`, top: `${hoverPreview.y}%`, transform: 'translate(0, -85%)', fontSize: activePlacement.fontSize }}
                >
                  {activePlacement.text}
                </div>
              ) : null}
              {overlays.filter((overlay) => overlay.page === index).map((overlay) => (
                <div
                  key={overlay.id}
                  className={cn('absolute cursor-move text-slate-900', overlay.type === 'wtn' ? 'font-mono font-bold' : 'bg-transparent shadow-none', overlay.type === 'signature' ? 'font-serif italic text-blue-900' : 'font-medium', draggingOverlayId === overlay.id && 'opacity-70 outline outline-1 outline-sky-500 rounded-[4px]')}
                  style={{ left: `${overlay.x}%`, top: `${overlay.y}%`, transform: 'translate(0, -85%)', fontSize: overlay.fontSize || (overlay.type === 'signature' ? 15.5 : overlay.type === 'wtn' ? 11.2 : 9.6), touchAction: 'none' }}
                  onDoubleClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    updateOverlays(overlaysRef.current.filter((item) => item.id !== overlay.id), 'Label removal saved.');
                  }}
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const parent = event.currentTarget.parentElement;
                    if (!parent) return;
                    const rect = parent.getBoundingClientRect();
                    const startX = event.clientX;
                    const startY = event.clientY;
                    const initialX = overlay.x;
                    const initialY = overlay.y;
                    setDraggingOverlayId(overlay.id);
                    try {
                      event.currentTarget.setPointerCapture(event.pointerId);
                    } catch {}
                    const onMove = (moveEvent: PointerEvent) => {
                      const nextX = Math.min(98, Math.max(2, initialX + ((moveEvent.clientX - startX) / rect.width) * 100));
                      const nextY = Math.min(98, Math.max(2, initialY + ((moveEvent.clientY - startY) / rect.height) * 100));
                      const nextOverlays = overlaysRef.current.map((item) => item.id === overlay.id ? { ...item, x: nextX, y: nextY } : item);
                      overlaysRef.current = nextOverlays;
                      setOverlays(nextOverlays);
                    };
                    const onUp = () => {
                      setDraggingOverlayId(null);
                      queueOverlaySave(overlaysRef.current, 'Label position saved.');
                      window.removeEventListener('pointermove', onMove);
                      window.removeEventListener('pointerup', onUp);
                      window.removeEventListener('pointercancel', onUp);
                    };
                    window.addEventListener('pointermove', onMove, { passive: false });
                    window.addEventListener('pointerup', onUp);
                    window.addEventListener('pointercancel', onUp);
                  }}
                >
                  {overlay.text}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

function PacketView({ receipts, transactions, statementFile, onDownloadReceipt, onDownloadReceiptSheet, onExportPdf, updateTransactionWTM, availableWTNs, onReorderReceipt }: any) {
  const noop = async () => {};
  return (
    <div className="space-y-8">
      <section className="overflow-hidden rounded-[32px] border border-slate-200 bg-white text-slate-900 shadow-2xl shadow-black/20">
        <div className="flex flex-col gap-4 border-b border-slate-200 bg-slate-950 px-8 py-8 text-slate-900 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-3xl font-black tracking-tight text-slate-300">Monthly Statement</h2>
            <p className="mt-1 text-sm font-bold uppercase tracking-[0.28em] text-slate-500">Reconciliation Packet</p>
          </div>
          <button onClick={onExportPdf} className="inline-flex items-center gap-2 rounded-2xl bg-emerald-500 px-5 py-3 font-black text-slate-950"><Download className="h-4 w-4" />Download Packet PDF</button>
        </div>
        <div className="overflow-x-auto p-6 md:p-8">
          {statementFile && (
            <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-black uppercase tracking-[0.24em] text-slate-400">Attached statement PDF</p>
              <p className="mt-2 text-sm font-semibold text-slate-900">{statementFile.fileName}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <a href={statementFile.fileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 shadow-sm">
                  <FileText className="h-4 w-4" /> Open Statement
                </a>
              </div>
            </div>
          )}
          {transactions.length ? (
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b-2 border-slate-100">
                  <th className="px-4 py-4 text-xs font-black uppercase tracking-wider text-slate-400">Date</th>
                  <th className="px-4 py-4 text-xs font-black uppercase tracking-wider text-slate-400">Merchant</th>
                  <th className="px-4 py-4 text-xs font-black uppercase tracking-wider text-slate-400">Amount</th>
                  <th className="px-4 py-4 text-xs font-black uppercase tracking-wider text-slate-400">WTN #</th>
                  <th className="px-4 py-4 text-xs font-black uppercase tracking-wider text-slate-400">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t: Transaction) => {
                  const hasReceipt = receipts.some((r: Receipt) => r.wtmNumber === t.wtmNumber && t.wtmNumber !== '');
                  return (
                    <tr key={t.id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-4 font-mono text-sm text-slate-600">{format(parseISO(safeIsoDate(t.date)), 'MM/dd/yyyy')}</td>
                      <td className="px-4 py-4 font-bold text-slate-900">{t.merchant}</td>
                      <td className="px-4 py-4 font-black text-slate-900">${t.amount.toFixed(2)}</td>
                      <td className="px-4 py-4"><select value={t.wtmNumber} onChange={(e) => void updateTransactionWTM(t.id, e.target.value)} className="rounded-xl border border-slate-200 bg-white/95 px-3 py-2 text-sm font-mono shadow-sm"><option value="">Select WTN</option>{availableWTNs.map((wtn: string) => <option key={`${t.id}-packet-${wtn}`} value={wtn}>{wtn}</option>)}</select></td>
                      <td className="px-4 py-4">{hasReceipt ? <span className="inline-flex rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-black uppercase text-emerald-600">Matched</span> : <span className="inline-flex rounded-full bg-rose-50 px-2 py-1 text-[10px] font-black uppercase text-rose-600">No Receipt</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : statementFile ? null : <EmptyState title="No statement data" description="Import a statement to populate the monthly packet." />}
        </div>
      </section>

      <div className="space-y-2.5">
        {receipts.map((receipt: Receipt, index: number) => (
          <div key={receipt.id} className="space-y-1.5">
            <div className="flex justify-end gap-2 px-0.5">
              <button disabled={index === 0} onClick={() => void onReorderReceipt(receipt.id, 'up')} className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                <ArrowUp className="h-3.5 w-3.5" /> Up
              </button>
              <button disabled={index === receipts.length - 1} onClick={() => void onReorderReceipt(receipt.id, 'down')} className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                <ArrowDown className="h-3.5 w-3.5" /> Down
              </button>
            </div>
            <ReceiptCard
              receipt={receipt}
              updateReceipt={noop}
              updateReceiptWTM={noop}
              requestDelete={() => {}}
              onDownload={() => onDownloadReceiptSheet(receipt)}
              onPreview={() => onDownloadReceipt(`${receipt.storeName || 'receipt'}.jpg`, receipt.imageUrl)}
              allowEdit={false}
              allowDelete={false}
              previewLabel="Raw receipt"
              downloadLabel="Receipt PDF"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function InfoRow({ label, value, multiline = false, valueClassName = '', compact = false }: { label: string; value: React.ReactNode; multiline?: boolean; valueClassName?: string; compact?: boolean }) {
  return (
    <div className={cn('space-y-1.5', compact && 'space-y-1')}>
      <p className={cn('font-black uppercase text-slate-400', compact ? 'text-[9px] tracking-[0.18em]' : 'text-[11px] tracking-[0.24em]')}>{label}</p>
      <div className={cn(compact ? 'text-[11px] font-medium text-slate-700' : 'text-sm font-semibold text-slate-700', multiline && 'leading-relaxed', valueClassName)}>{value}</div>
    </div>
  );
}

function BottomMenu({ showMenu, setShowMenu, session, billingStatus, onManageBilling, onSignOut }: any) {
  return (
    <div className="fixed bottom-4 right-4 z-40">
      <AnimatePresence>
        {showMenu && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} className="mb-3 w-64 overflow-hidden rounded-3xl border border-slate-200 bg-white/95 shadow-2xl shadow-black/30 backdrop-blur-xl">
            <div className="border-b border-slate-200 px-4 py-3">
              <p className="text-xs uppercase tracking-[0.26em] text-slate-400">Account</p>
              <p className="mt-1 truncate text-sm font-bold text-slate-700">{session?.user?.email || 'Signed in'}</p>
              <p className="mt-2 text-xs font-semibold text-slate-500">Subscription: {billingStatus?.status || 'not active'}</p>
            </div>
            <button onClick={() => void onManageBilling()} className="flex w-full items-center gap-2 px-4 py-4 text-left text-sm font-bold text-slate-700 hover:bg-white">Manage subscription</button>
            <button onClick={() => void onSignOut()} className="flex w-full items-center gap-2 px-4 py-4 text-left text-sm font-bold text-slate-700 hover:bg-white"><LogOut className="h-4 w-4" />Sign out</button>
          </motion.div>
        )}
      </AnimatePresence>
      <button onClick={() => setShowMenu((v: boolean) => !v)} className="rounded-full bg-emerald-500 p-4 text-slate-950 shadow-xl shadow-emerald-500/25"><Menu className="h-5 w-5" /></button>
    </div>
  );
}

function GuidedFlowPanel({ viewMode, uploadReceiptsComplete, reviewReceiptsComplete, uploadStatementComplete, labelPdfComplete, packetReady, missingReceiptFieldsCount, unplacedReceiptWTNsCount, onOpenReceipts, onOpenStatement, onOpenLabelPdf, onOpenGenerate, onGenerateNow }: any) {
  const steps = [
    { key: 'receipts', label: 'Receipts', complete: uploadReceiptsComplete, active: !uploadReceiptsComplete, onClick: onOpenReceipts },
    { key: 'review', label: 'Review', complete: reviewReceiptsComplete, active: uploadReceiptsComplete && !reviewReceiptsComplete, onClick: onOpenReceipts },
    { key: 'statement', label: 'Statement', complete: uploadStatementComplete, active: reviewReceiptsComplete && !uploadStatementComplete, onClick: onOpenStatement },
    { key: 'label', label: 'Label PDF', complete: labelPdfComplete, active: uploadStatementComplete && !labelPdfComplete, onClick: onOpenLabelPdf },
    { key: 'generate', label: 'Generate', complete: packetReady, active: packetReady || viewMode === 'package', onClick: onOpenGenerate },
  ];

  let nextTitle = 'Next step: Upload your receipts';
  let nextSubtext = 'Add at least one receipt to begin your packet.';
  let nextAction = onOpenReceipts;
  let nextButtonLabel = 'Open Receipts';

  if (uploadReceiptsComplete && !reviewReceiptsComplete) {
    nextTitle = 'Next step: Review your receipts';
    nextSubtext = `Complete ${missingReceiptFieldsCount} missing receipt field${missingReceiptFieldsCount === 1 ? '' : 's'} to finish this step.`;
    nextAction = onOpenReceipts;
    nextButtonLabel = 'Review Receipts';
  } else if (reviewReceiptsComplete && !uploadStatementComplete) {
    nextTitle = 'Next step: Upload your statement';
    nextSubtext = 'Upload the statement PDF for this month to continue.';
    nextAction = onOpenStatement;
    nextButtonLabel = 'Upload Statement';
  } else if (uploadStatementComplete && !labelPdfComplete) {
    nextTitle = 'Next step: Label your statement';
    nextSubtext = `Place ${unplacedReceiptWTNsCount} WTN label${unplacedReceiptWTNsCount === 1 ? '' : 's'} on your PDF to finish this step.`;
    nextAction = onOpenLabelPdf;
    nextButtonLabel = 'Open Label PDF';
  } else if (packetReady) {
    nextTitle = 'Your packet is ready';
    nextSubtext = '';
    nextAction = onGenerateNow;
    nextButtonLabel = 'Generate Packet';
  }

  return (
    <div className="mb-4 rounded-[24px] border border-slate-200 bg-white/85 px-4 py-4 shadow-sm">
      <div className="flex gap-3 overflow-x-auto pb-2">
        {steps.map((step, index) => {
          const isFuture = !step.complete && !step.active;
          const isClickable = !isFuture;
          return (
            <div key={step.key} className="flex min-w-fit items-center gap-3">
              <button
                type="button"
                onClick={step.onClick}
                disabled={!isClickable}
                className={`flex items-center gap-2 rounded-full px-3 py-2 text-sm font-bold transition ${step.complete ? 'bg-emerald-100 text-emerald-900' : step.active ? 'bg-emerald-500 text-slate-950' : 'bg-slate-100 text-slate-400'} ${isClickable ? 'cursor-pointer hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-2' : 'cursor-not-allowed opacity-70'}`}
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full border border-current text-[11px]">{step.complete ? '✓' : index + 1}</span>
                <span className="whitespace-nowrap">{step.label}</span>
              </button>
              {index < steps.length - 1 && <div className={`h-px w-4 shrink-0 ${isFuture ? 'bg-slate-200' : 'bg-emerald-300'}`} />}
            </div>
          );
        })}
      </div>

      <div className="mt-4 rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-4">
        <p className="text-lg font-black text-slate-900">{nextTitle}</p>
        {nextSubtext ? <p className="mt-1 text-sm text-slate-600">{nextSubtext}</p> : null}
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <button onClick={nextAction} className="inline-flex items-center justify-center rounded-2xl bg-emerald-500 px-5 py-3 text-sm font-black text-slate-950 shadow-[0_12px_30px_rgba(16,185,129,0.28)] hover:bg-emerald-400">
            {nextButtonLabel}
          </button>
          {!packetReady && <button onClick={onOpenGenerate} className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50">Generate Packet</button>}
        </div>
      </div>
    </div>
  );
}

function BillingBanner({ billingStatus, isBillingLoading, isCheckoutLoading, onCheckout, onManageBilling }: any) {
  const monthlyPriceId = import.meta.env.VITE_STRIPE_MONTHLY_PRICE_ID || '';
  const yearlyPriceId = import.meta.env.VITE_STRIPE_YEARLY_PRICE_ID || '';

  if (isBillingLoading) {
    return <div className="mb-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-600">Checking subscription status...</div>;
  }

  if (billingStatus?.hasSubscription) {
    return (
      <div className="mb-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-slate-900">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-bold">Subscription active</p>
            <p className="text-slate-700">Packet generation is enabled{billingStatus.currentPeriodEnd ? ` until ${new Date(billingStatus.currentPeriodEnd).toLocaleDateString()}` : ''}.</p>
          </div>
          <button onClick={() => void onManageBilling()} disabled={isCheckoutLoading} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 disabled:opacity-50">Manage subscription</button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-2xl border border-amber-300/60 bg-amber-50 px-4 py-4 text-sm text-slate-900">
      <p className="font-bold">Subscription required for packet generation</p>
      <p className="mt-1 text-slate-700">Users can upload and organize receipts, but generating the final packet requires an active Stripe subscription. Every new subscription starts with a 30 day free trial.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={() => void onCheckout(monthlyPriceId)} disabled={!monthlyPriceId || isCheckoutLoading} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">{isCheckoutLoading ? 'Opening Stripe...' : 'Start monthly plan, 30 day free trial'}</button>
        <button onClick={() => void onCheckout(yearlyPriceId)} disabled={!yearlyPriceId || isCheckoutLoading} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700 disabled:opacity-50">Start yearly plan, 30 day free trial</button>
      </div>
    </div>
  );
}

function ConfirmModal({ title, description, confirmLabel, onCancel, onConfirm }: { title: string; description: string; confirmLabel: string; onCancel: () => void; onConfirm: () => void }) {
  const lines = description.split('\n').filter(Boolean);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-slate-100/70 px-4">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }} className="w-full max-w-md rounded-[28px] border border-slate-200 bg-white p-6 shadow-2xl shadow-black/30">
        <h3 className="text-xl font-black">{title}</h3>
        {lines.length > 1 ? (
          <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-slate-900/70">
            {lines.map((line, index) => <li key={`${line}-${index}`}>{line}</li>)}
          </ul>
        ) : (
          <p className="mt-2 text-sm text-slate-900/60">{description}</p>
        )}
        <div className="mt-6 flex gap-3">
          <button onClick={onCancel} className="flex-1 rounded-2xl border border-slate-200 px-4 py-3 font-bold text-slate-700">Cancel</button>
          <button onClick={onConfirm} className="flex-1 rounded-2xl bg-rose-500 px-4 py-3 font-black text-slate-900">{confirmLabel}</button>
        </div>
      </motion.div>
    </motion.div>
  );
}
